import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PdMark from '../components/PdMark';
import { publicApi } from '../lib/api';
import { StatusPill } from '../components/StatusPills';
import AppFooter from '../components/AppFooter';

const TYPE_LABELS = {
  solicitor: 'Solicitor',
  legal_advocate: 'Legal advocate',
  support_worker: 'Support worker',
  counsellor: 'Counsellor',
  therapist: 'Therapist',
  gp_doctor: 'GP / doctor',
  family_member: 'Family member',
  friend: 'Friend',
  other: 'Other',
};

const PERM_LABELS = { read: 'Read only', read_write: 'Read and write' };


const Verify = () => {
  const { reference } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    publicApi
      .verifyAuthorisation(reference)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'not_found' : 'error'))
      .finally(() => setLoading(false));
  }, [reference]);

  return (
    <div className="min-h-screen bg-ground flex flex-col text-ink">
      <header className="px-4 sm:px-7 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-3 rounded-md" aria-label="Precious Data home">
            <PdMark size={28} className="text-brand" />
            <span className="font-heading text-[18px] font-bold tracking-[-0.02em] whitespace-nowrap">Precious Data</span>
          </Link>
          <Link to="/" className="text-[15px] text-ink-70 hover:text-ink transition-colors duration-state">
            preciousdata.org.uk
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-7 py-6 sm:py-10 w-full" data-testid="verify-page">
        <div className="bg-surface rounded-xl p-7 sm:p-9 space-y-8">
          <div className="space-y-2">
            <p className="pd-eyebrow">Authorisation reference</p>
            <p className="pd-mono text-[22px] font-medium text-ink normal-case" data-testid="verify-reference">
              {reference}
            </p>
          </div>

          {loading && (
            <p className="text-ink-70">Checking the reference…</p>
          )}

          {!loading && error === 'not_found' && (
            <NotFoundCard reference={reference} />
          )}

          {!loading && error === 'error' && (
            <ErrorCard />
          )}

          {!loading && data && <DetailCard data={data} />}

          <p className="text-sm text-ink-55 leading-relaxed max-w-xl">
            This page exists so organisations receiving an authorisation document can
            confirm independently that it is genuine and still in force. No other
            personal data is shown here.
          </p>
        </div>
      </main>

      <AppFooter />
    </div>
  );
};


const STATUS_LABELS = {
  pending: ['needs', 'Waiting for the invitee to sign up'],
  awaiting_acceptance: ['needs', 'Waiting for the invitee to accept'],
  declined: ['breach', 'Declined by the invitee — not in force'],
  left: ['breach', 'Access removed by the invitee — not in force'],
  expired: ['breach', 'Expired unaccepted — not in force'],
  revoked: ['breach', 'Revoked'],
};

const StatusBadge = ({ status }) => {
  if (status === 'active') {
    return <StatusPill tone="done">Active</StatusPill>;
  }
  const [tone, label] = STATUS_LABELS[status] || ['breach', 'Not in force'];
  return <StatusPill tone={tone}>{label}</StatusPill>;
};


const Field = ({ label, value }) => (
  <div className="space-y-1">
    <p className="pd-eyebrow">{label}</p>
    <p className="text-ink font-medium" data-testid={`verify-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {value || '—'}
    </p>
  </div>
);


const DetailCard = ({ data }) => {
  const typeLabel = data.invitee_type === 'other' && data.invitee_type_other
    ? data.invitee_type_other
    : (TYPE_LABELS[data.invitee_type] || data.invitee_type || '—');
  const permLabel = PERM_LABELS[data.permission] || data.permission || '—';

  let granted = '—';
  if (data.granted_at) {
    try {
      granted = new Date(data.granted_at).toLocaleString(undefined, {
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { /* fallback */ }
  }
  let revoked = null;
  if (data.revoked_at) {
    try {
      revoked = new Date(data.revoked_at).toLocaleString(undefined, {
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { revoked = data.revoked_at; }
  }

  return (
    <div className="bg-ground rounded-lg p-6 sm:p-8 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="font-heading text-[20px] font-semibold tracking-[-0.02em] text-ink">
          Authorisation to act on behalf of the data subject
        </p>
        <StatusBadge status={data.status} />
      </div>

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
        <Field label="Data subject" value={data.data_subject} />
        <Field label="Authorised person" value={data.authorised_person} />
        <Field label="Authorised person type" value={typeLabel} />
        <Field label="Permission level" value={permLabel} />
        <Field label="Date granted" value={granted} />
        {revoked && <Field label="Date revoked" value={revoked} />}
      </div>

      <div className="border-t border-rule pt-5">
        <p className="pd-eyebrow">Owner public key fingerprint (SHA-256)</p>
        <p className="pd-mono text-ink mt-1.5 break-all normal-case" data-testid="verify-fingerprint">
          {data.owner_pubkey_fingerprint || '—'}
        </p>
      </div>
    </div>
  );
};


const NotFoundCard = ({ reference }) => (
  <div className="bg-breach-wash text-breach-ink rounded-lg p-6 space-y-2">
    <p className="font-semibold">Authorisation not found</p>
    <p className="text-[15px]">
      There is no authorisation with the reference{' '}
      <span className="font-mono">{reference}</span>. Check the reference
      on the document and try again.
    </p>
  </div>
);


const ErrorCard = () => (
  <div className="bg-amber-wash text-amber-ink rounded-lg p-6 space-y-2">
    <p className="font-semibold">We could not reach the verification service</p>
    <p className="text-[15px]">
      Try again in a moment.
    </p>
  </div>
);


export default Verify;
